import React, { useState, useMemo } from 'react';

export interface HeatmapCell {
    /** Location/bin code */
    code: string;
    /** Display label (shortened) */
    label?: string;
    /** Value driving the color (0-100 or raw) */
    value: number;
    /** Optional row identifier (e.g. rack number) */
    row?: string;
    /** Optional column identifier (e.g. shelf position) */
    col?: string;
    /** Extra info for tooltip */
    details?: string;
}

export interface HeatmapBlockData {
    title?: string;
    cells: HeatmapCell[];
    /** Color scale: 'heat' (green→yellow→red), 'blue' (light→dark), 'utilization' (empty→full) */
    colorScale?: 'heat' | 'blue' | 'utilization';
    /** What the value represents */
    valueLabel?: string;
    /** Max value for scaling (auto-detected if omitted) */
    maxValue?: number;
}

interface HeatmapBlockProps {
    data: HeatmapBlockData;
    /** Callback when a cell is clicked — used for drill-down */
    onCellClick?: (cell: HeatmapCell) => void;
}

function getHeatColor(pct: number, scale: string): string {
    // pct is 0-100
    if (scale === 'blue') {
        const intensity = Math.round(40 + (pct / 100) * 180);
        return `rgb(${Math.max(0, 59 - pct * 0.5)}, ${Math.max(0, 130 - pct * 0.6)}, ${intensity + 50})`;
    }

    if (scale === 'utilization') {
        if (pct < 10) return '#1e293b'; // empty — dark
        if (pct < 30) return '#166534'; // low — green
        if (pct < 60) return '#22c55e'; // medium — bright green
        if (pct < 80) return '#facc15'; // high — yellow
        if (pct < 95) return '#f97316'; // very high — orange
        return '#ef4444'; // near full — red
    }

    // Default 'heat' scale: green → yellow → red
    if (pct < 10) return '#1e293b';
    if (pct < 25) return '#166534';
    if (pct < 50) return '#22c55e';
    if (pct < 70) return '#eab308';
    if (pct < 85) return '#f97316';
    return '#ef4444';
}

export const HeatmapBlock: React.FC<HeatmapBlockProps> = ({ data, onCellClick }) => {
    const [hoveredCell, setHoveredCell] = useState<HeatmapCell | null>(null);
    const scale = data.colorScale || 'heat';

    // Group cells into a grid by row/col if available
    const { grid, rows, cols, maxVal } = useMemo(() => {
        const maxVal = data.maxValue || Math.max(...data.cells.map(c => c.value), 1);

        // If cells have row/col, build a proper grid
        if (data.cells.some(c => c.row && c.col)) {
            const rowSet = new Set<string>();
            const colSet = new Set<string>();
            const cellMap = new Map<string, HeatmapCell>();

            for (const cell of data.cells) {
                if (cell.row && cell.col) {
                    rowSet.add(cell.row);
                    colSet.add(cell.col);
                    cellMap.set(`${cell.row}-${cell.col}`, cell);
                }
            }

            const rows = Array.from(rowSet).sort();
            const cols = Array.from(colSet).sort();
            const grid: (HeatmapCell | null)[][] = rows.map(r =>
                cols.map(c => cellMap.get(`${r}-${c}`) || null)
            );

            return { grid, rows, cols, maxVal };
        }

        // Flat list — try to arrange in a square-ish grid
        const count = data.cells.length;
        const colCount = Math.ceil(Math.sqrt(count));
        const grid: (HeatmapCell | null)[][] = [];
        const rows: string[] = [];
        const cols: string[] = Array.from({ length: colCount }, (_, i) => String(i + 1));

        for (let i = 0; i < count; i += colCount) {
            const row = data.cells.slice(i, i + colCount);
            const paddedRow = [...row, ...Array(colCount - row.length).fill(null)];
            grid.push(paddedRow);
            rows.push(String(grid.length));
        }

        return { grid, rows, cols, maxVal };
    }, [data.cells, data.maxValue]);

    if (data.cells.length === 0) {
        return <p className="text-sm text-gray-500 py-2">Ingen data for varmekart</p>;
    }

    return (
        <div className="mt-2">
            {data.title && (
                <h4 className="text-sm font-semibold text-gray-700 mb-2">{data.title}</h4>
            )}

            <div className="overflow-x-auto">
                <div className="inline-block min-w-[300px]">
                    {/* Column headers */}
                    {data.cells.some(c => c.col) && (
                        <div className="flex gap-[3px] mb-1 ml-10">
                            {cols.map(c => (
                                <div
                                    key={c}
                                    className="flex-1 text-center text-[9px] text-gray-400 font-medium"
                                    style={{ minWidth: 28 }}
                                >
                                    {c}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Grid rows */}
                    {grid.map((row, rowIdx) => (
                        <div key={rowIdx} className="flex gap-[3px] items-center mb-[3px]">
                            {/* Row label */}
                            {data.cells.some(c => c.row) && (
                                <div className="w-8 text-right text-[9px] text-gray-400 font-medium pr-1">
                                    {rows[rowIdx]}
                                </div>
                            )}

                            {row.map((cell, colIdx) => {
                                if (!cell) {
                                    return (
                                        <div
                                            key={colIdx}
                                            className="flex-1 aspect-square rounded"
                                            style={{ minWidth: 28, background: '#0f172a' }}
                                        />
                                    );
                                }

                                const pct = maxVal > 0 ? (cell.value / maxVal) * 100 : 0;
                                const color = getHeatColor(pct, scale);

                                return (
                                    <div
                                        key={colIdx}
                                        className={`flex-1 aspect-square rounded flex items-center justify-center text-[9px] font-semibold transition-all relative ${
                                            onCellClick ? 'cursor-pointer' : ''
                                        }`}
                                        style={{
                                            minWidth: 28,
                                            background: color,
                                            color: pct > 50 ? 'white' : '#94a3b8',
                                        }}
                                        onClick={() => onCellClick?.(cell)}
                                        onMouseEnter={() => setHoveredCell(cell)}
                                        onMouseLeave={() => setHoveredCell(null)}
                                        title={`${cell.code}: ${cell.value}${data.valueLabel ? ` ${data.valueLabel}` : ''}`}
                                    >
                                        {cell.label || cell.code.slice(-3)}

                                        {/* Tooltip */}
                                        {hoveredCell === cell && (
                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg px-2.5 py-1.5 text-[10px] whitespace-nowrap z-20 text-gray-700">
                                                <div className="font-semibold">{cell.code}</div>
                                                <div>{cell.value.toLocaleString('nb-NO')} {data.valueLabel || ''}</div>
                                                {cell.details && <div className="text-gray-500">{cell.details}</div>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-2 mt-3 justify-center">
                <span className="text-[10px] text-gray-400">Lav</span>
                <div
                    className="w-40 h-2 rounded-full"
                    style={{
                        background: scale === 'blue'
                            ? 'linear-gradient(to right, #1e3a5f, #3b82f6)'
                            : 'linear-gradient(to right, #1e293b, #166534, #22c55e, #facc15, #ef4444)',
                    }}
                />
                <span className="text-[10px] text-gray-400">Høy</span>
            </div>
        </div>
    );
};
